import {
  createDeliverableSchema,
  createEpicSchema,
  createGoalSchema,
  createTaskSchema,
  patchDeliverableSchema,
  patchEpicSchema,
  patchGoalSchema,
  patchTaskSchema,
} from '@mona-roadmap/domain';
import type {
  CreateDeliverable,
  CreateEpic,
  CreateGoal,
  CreateTask,
  Deliverable,
  Epic,
  Metadata,
  PersonRef,
  ProductGoal,
  RoadmapSnapshot,
  Task,
} from '@mona-roadmap/domain';
import type { QueryClient } from '@tanstack/react-query';
import type { ZodError } from 'zod';
import { api, describeError, isApiError } from './api';
import { fieldKey, isLocked, markError, markSaved, markSaving } from './fieldState';
import {
  applyDeliverablePatch,
  applyEpicPatch,
  applyGoalPatch,
  deliverableKeysTouched,
  epicKeysTouched,
  expectedForDeliverable,
  expectedForEpic,
  expectedForGoal,
  goalKeysTouched,
  isTempId,
  makeTempDeliverable,
  newTempId,
  removeDeliverable,
  removeEpic,
  removeGoal,
  rollbackDeliverable,
  rollbackEpic,
  rollbackGoal,
  upsertDeliverable,
  upsertEpic,
  upsertGoal,
  type DeliverableChanges,
  type EpicChanges,
  type GoalChanges,
} from './optimistic';
import { beginWrite, endWrite } from './inflight';
import { KEYS, refreshRoadmap } from './queries';
import { toast } from './toast';

export type Result = { ok: true; id?: string } | { ok: false; message: string };

export interface RevealRequest {
  kind: 'deliverable' | 'goal' | 'epic';
  id: string;
  /** Move keyboard focus to the row (default true). Off while a drawer/dialog holds focus. */
  focus?: boolean;
}
export interface CreateHooks {
  /** Called right after the optimistic insert (temporary id). */
  onInserted?: (r: RevealRequest) => void;
  /** Called after the server answered (real id). */
  onCreated?: (r: RevealRequest) => void;
}

const firstIssue = (e: ZodError): string => {
  const i = e.issues[0];
  if (!i) return 'Invalid value';
  const path = i.path.filter((p) => p !== 'expected').join('.');
  return path ? `${path}: ${i.message}` : i.message;
};

interface PatchPlan<E> {
  kind: 'd' | 'g' | 'e' | 't';
  id: string;
  fields: string[];
  label: string;
  validate: (expected: Record<string, unknown>) => string | null;
  entity: () => E | undefined;
  expected: (before: E) => Record<string, unknown>;
  optimistic: () => void;
  rollback: (before: E, optimistic: E) => void;
  send: (expected: Record<string, unknown>) => Promise<E>;
  commit: (server: E) => void;
}

/**
 * All write operations of the UI. Each one: validates with the shared zod schema, takes an
 * in-flight lock per item+field, updates the cache optimistically, sends only the changed fields
 * plus `expected`, rolls back on failure and refreshes the roadmap once nothing else is in flight.
 */
export function createMutations(qc: QueryClient) {
  const getSnap = () => qc.getQueryData<RoadmapSnapshot>(KEYS.roadmap);
  const setSnap = (fn: (s: RoadmapSnapshot) => RoadmapSnapshot) => qc.setQueryData<RoadmapSnapshot>(KEYS.roadmap, (s) => (s ? fn(s) : s));
  const people = (): PersonRef[] => qc.getQueryData<Metadata>(KEYS.metadata)?.people ?? [];
  const createLocks = new Set<string>();
  let inFlight = 0;

  const settle = (extra?: () => void) => {
    inFlight--;
    endWrite();
    extra?.();
    // Re-read from monday itself once nothing else is saving (bypasses a possibly stale server cache).
    if (inFlight === 0) void refreshRoadmap(qc).catch(() => qc.invalidateQueries({ queryKey: KEYS.roadmap }));
  };

  async function runPatch<E>(plan: PatchPlan<E>): Promise<Result> {
    const keys = plan.fields.map((f) => fieldKey(plan.kind, plan.id, f));
    if (isTempId(plan.id)) return { ok: false, message: 'This item is still being created' };
    if (keys.some(isLocked)) return { ok: false, message: 'A save is already in progress for this field' };
    const before = plan.entity();
    if (!before) return { ok: false, message: 'This item no longer exists' };
    const expected = plan.expected(before);
    const invalid = plan.validate(expected);
    if (invalid) {
      markError(keys, invalid);
      toast('error', `${plan.label}: ${invalid}`);
      return { ok: false, message: invalid };
    }
    markSaving(keys);
    inFlight++;
    beginWrite();
    plan.optimistic();
    const optimistic = plan.entity() as E;
    try {
      const server = await plan.send(expected);
      plan.commit(server);
      markSaved(keys);
      return { ok: true };
    } catch (e) {
      plan.rollback(before, optimistic);
      const message = describeError(e);
      markError(keys, message);
      toast('error', `${plan.label}: ${message}`);
      if (isApiError(e) && e.code === 'CONFLICT') void refreshRoadmap(qc).catch(() => undefined);
      return { ok: false, message };
    } finally {
      settle();
    }
  }

  // ---- deliverables ----------------------------------------------------------------------------

  const findDeliverable = (id: string) => getSnap()?.deliverables.find((d) => d.id === id);

  function patchDeliverable(id: string, changes: DeliverableChanges): Promise<Result> {
    return runPatch<Deliverable>({
      kind: 'd',
      id,
      fields: Object.keys(changes),
      label: findDeliverable(id)?.name ?? 'Deliverable',
      validate: (expected) => {
        const r = patchDeliverableSchema.safeParse({ ...changes, expected });
        return r.success ? null : firstIssue(r.error);
      },
      entity: () => findDeliverable(id),
      expected: (d) => expectedForDeliverable(d, changes),
      optimistic: () => setSnap((s) => applyDeliverablePatch(s, id, changes, people())),
      rollback: (before, optimistic) => setSnap((s) => rollbackDeliverable(s, id, deliverableKeysTouched(changes), before, optimistic)),
      send: async (expected) => (await api.patchDeliverable(id, { ...changes, expected })).deliverable,
      commit: (server) => setSnap((s) => upsertDeliverable(s, server)),
    });
  }

  async function createDeliverable(input: CreateDeliverable, hooks: CreateHooks = {}): Promise<Result> {
    const parsed = createDeliverableSchema.safeParse(input);
    if (!parsed.success) {
      const message = firstIssue(parsed.error);
      toast('error', `Cannot create deliverable: ${message}`);
      return { ok: false, message };
    }
    const lock = `d|${input.name}|${input.productGoalId ?? ''}|${input.epicId ?? ''}`;
    const snap = getSnap();
    if (!snap) return { ok: false, message: 'Roadmap not loaded' };
    if (createLocks.has(lock)) return { ok: false, message: 'Already creating this deliverable' };
    createLocks.add(lock);
    inFlight++;
    beginWrite();
    const tempId = newTempId();
    setSnap((s) => upsertDeliverable(s, makeTempDeliverable(parsed.data, tempId, s, people())));
    hooks.onInserted?.({ kind: 'deliverable', id: tempId });
    try {
      const { deliverable } = await api.createDeliverable(parsed.data);
      setSnap((s) => upsertDeliverable(s, deliverable, tempId));
      hooks.onCreated?.({ kind: 'deliverable', id: deliverable.id });
      toast('success', `Created "${deliverable.name}"`);
      return { ok: true, id: deliverable.id };
    } catch (e) {
      setSnap((s) => removeDeliverable(s, tempId));
      const message = describeError(e);
      toast('error', `Could not create "${input.name}": ${message}`);
      return { ok: false, message };
    } finally {
      createLocks.delete(lock);
      settle();
    }
  }

  // ---- product goals ------------------------------------------------------------------------------

  const findGoal = (id: string) => getSnap()?.productGoals.find((g) => g.id === id);

  function patchGoal(id: string, changes: GoalChanges): Promise<Result> {
    return runPatch<ProductGoal>({
      kind: 'g',
      id,
      fields: Object.keys(changes),
      label: findGoal(id)?.name ?? 'Product goal',
      validate: (expected) => {
        const r = patchGoalSchema.safeParse({ ...changes, expected });
        return r.success ? null : firstIssue(r.error);
      },
      entity: () => findGoal(id),
      expected: (g) => expectedForGoal(g, changes),
      optimistic: () => setSnap((s) => applyGoalPatch(s, id, changes)),
      rollback: (before, optimistic) => setSnap((s) => rollbackGoal(s, id, goalKeysTouched(changes), before, optimistic)),
      send: async (expected) => (await api.patchGoal(id, { ...changes, expected })).productGoal,
      commit: (server) => setSnap((s) => upsertGoal(s, server)),
    });
  }

  async function createGoal(input: CreateGoal, hooks: CreateHooks = {}): Promise<Result> {
    const parsed = createGoalSchema.safeParse(input);
    if (!parsed.success) {
      const message = firstIssue(parsed.error);
      toast('error', `Cannot create product goal: ${message}`);
      return { ok: false, message };
    }
    const lock = `g|${input.name}`;
    if (!getSnap()) return { ok: false, message: 'Roadmap not loaded' };
    if (createLocks.has(lock)) return { ok: false, message: 'Already creating this product goal' };
    createLocks.add(lock);
    inFlight++;
    beginWrite();
    const tempId = newTempId();
    const temp: ProductGoal = {
      id: tempId,
      name: input.name,
      url: '',
      targetDate: input.targetDate ?? null,
      status: input.status ?? null,
      description: null,
      position: (getSnap()?.productGoals.reduce((m, g) => Math.max(m, g.position), 0) ?? 0) + 1,
      updatedAt: null,
    };
    setSnap((s) => upsertGoal(s, temp));
    hooks.onInserted?.({ kind: 'goal', id: tempId });
    try {
      const { productGoal } = await api.createGoal(parsed.data);
      setSnap((s) => upsertGoal(s, productGoal, tempId));
      hooks.onCreated?.({ kind: 'goal', id: productGoal.id });
      toast('success', `Created product goal "${productGoal.name}"`);
      return { ok: true, id: productGoal.id };
    } catch (e) {
      setSnap((s) => removeGoal(s, tempId));
      const message = describeError(e);
      toast('error', `Could not create "${input.name}": ${message}`);
      return { ok: false, message };
    } finally {
      createLocks.delete(lock);
      settle();
    }
  }

  // ---- epics --------------------------------------------------------------------------------------

  const findEpic = (id: string) => getSnap()?.epics.find((e) => e.id === id);

  function patchEpic(id: string, changes: EpicChanges): Promise<Result> {
    return runPatch<Epic>({
      kind: 'e',
      id,
      fields: Object.keys(changes),
      label: findEpic(id)?.name ?? 'Epic',
      validate: (expected) => {
        const r = patchEpicSchema.safeParse({ ...changes, expected });
        return r.success ? null : firstIssue(r.error);
      },
      entity: () => findEpic(id),
      expected: (e) => expectedForEpic(e, changes),
      optimistic: () => setSnap((s) => applyEpicPatch(s, id, changes, people())),
      rollback: (before, optimistic) => setSnap((s) => rollbackEpic(s, id, epicKeysTouched(changes), before, optimistic)),
      send: async (expected) => (await api.patchEpic(id, { ...changes, expected })).epic,
      commit: (server) => setSnap((s) => upsertEpic(s, server)),
    });
  }

  async function createEpic(input: CreateEpic, hooks: CreateHooks = {}): Promise<Result> {
    const parsed = createEpicSchema.safeParse(input);
    if (!parsed.success) {
      const message = firstIssue(parsed.error);
      toast('error', `Cannot create epic: ${message}`);
      return { ok: false, message };
    }
    const lock = `e|${input.name}`;
    if (!getSnap()) return { ok: false, message: 'Roadmap not loaded' };
    if (createLocks.has(lock)) return { ok: false, message: 'Already creating this epic' };
    createLocks.add(lock);
    inFlight++;
    beginWrite();
    const tempId = newTempId();
    const byId = new Map(people().map((p) => [p.id, p]));
    const pick = (ids: string[] | undefined) => (ids ?? []).map((i) => byId.get(i) ?? { id: i, name: i, kind: 'person' as const });
    const temp: Epic = {
      id: tempId,
      name: input.name,
      url: '',
      productArea: input.productArea ?? null,
      type: input.type ?? null,
      pmOwners: pick(input.pmOwnerIds),
      rdOwners: pick(input.rdOwnerIds),
      description: null,
      position: (getSnap()?.epics.reduce((m, e) => Math.max(m, e.position), 0) ?? 0) + 1,
      updatedAt: null,
    };
    setSnap((s) => upsertEpic(s, temp));
    hooks.onInserted?.({ kind: 'epic', id: tempId });
    try {
      const { epic } = await api.createEpic(parsed.data);
      setSnap((s) => upsertEpic(s, epic, tempId));
      hooks.onCreated?.({ kind: 'epic', id: epic.id });
      toast('success', `Created epic "${epic.name}"`);
      return { ok: true, id: epic.id };
    } catch (e) {
      setSnap((s) => removeEpic(s, tempId));
      const message = describeError(e);
      toast('error', `Could not create "${input.name}": ${message}`);
      return { ok: false, message };
    } finally {
      createLocks.delete(lock);
      settle();
    }
  }

  // ---- tasks (monday subitems) -------------------------------------------------------------------------

  const tasksOf = (deliverableId: string) => qc.getQueryData<Task[]>(KEYS.tasks(deliverableId));
  const setTasks = (deliverableId: string, fn: (t: Task[]) => Task[]) => qc.setQueryData<Task[]>(KEYS.tasks(deliverableId), (t) => (t ? fn(t) : t));
  const bumpSubitems = (deliverableId: string, by: number) =>
    setSnap((s) => ({ ...s, deliverables: s.deliverables.map((d) => (d.id === deliverableId ? { ...d, subitemCount: Math.max(0, d.subitemCount + by) } : d)) }));

  async function createTask(deliverableId: string, input: CreateTask): Promise<Result> {
    const parsed = createTaskSchema.safeParse(input);
    if (!parsed.success) {
      const message = firstIssue(parsed.error);
      toast('error', `Cannot add task: ${message}`);
      return { ok: false, message };
    }
    if (isTempId(deliverableId)) return { ok: false, message: 'The deliverable is still being created' };
    const lock = `t|${deliverableId}|${input.name}`;
    if (createLocks.has(lock)) return { ok: false, message: 'Already adding this task' };
    createLocks.add(lock);
    inFlight++;
    beginWrite();
    const tempId = newTempId();
    const temp: Task = { id: tempId, name: input.name, url: '', deliverableId, status: input.status ?? null, owners: [], date: input.date ?? null };
    setTasks(deliverableId, (t) => [...t, temp]);
    bumpSubitems(deliverableId, 1);
    try {
      const { task } = await api.createTask(deliverableId, parsed.data);
      setTasks(deliverableId, (t) => t.map((x) => (x.id === tempId ? task : x)));
      return { ok: true, id: task.id };
    } catch (e) {
      setTasks(deliverableId, (t) => t.filter((x) => x.id !== tempId));
      bumpSubitems(deliverableId, -1);
      const message = describeError(e);
      toast('error', `Could not add task "${input.name}": ${message}`);
      return { ok: false, message };
    } finally {
      createLocks.delete(lock);
      settle(() => void qc.invalidateQueries({ queryKey: KEYS.tasks(deliverableId) }));
    }
  }

  type TaskChanges = Partial<Pick<Task, 'name' | 'status' | 'date'>> & { ownerIds?: string[] };

  function patchTask(task: Task, changes: TaskChanges): Promise<Result> {
    const id = task.id;
    const body = { ...changes };
    const apply = (t: Task): Task => ({
      ...t,
      ...(changes.name !== undefined ? { name: changes.name } : {}),
      ...(changes.status !== undefined ? { status: changes.status } : {}),
      ...(changes.date !== undefined ? { date: changes.date } : {}),
      ...(changes.ownerIds !== undefined ? { owners: changes.ownerIds.map((oid) => people().find((p) => p.id === oid) ?? { id: oid, name: oid, kind: 'person' as const }) } : {}),
    });
    const current = () => tasksOf(task.deliverableId)?.find((t) => t.id === id);
    return runPatch<Task>({
      kind: 't',
      id,
      fields: Object.keys(changes),
      label: task.name,
      validate: (expected) => {
        const r = patchTaskSchema.safeParse({ ...body, expected });
        return r.success ? null : firstIssue(r.error);
      },
      entity: current,
      expected: (t) => ({
        ...(changes.name !== undefined ? { name: t.name } : {}),
        ...(changes.status !== undefined ? { status: t.status } : {}),
        ...(changes.date !== undefined ? { date: t.date } : {}),
        ...(changes.ownerIds !== undefined ? { ownerIds: t.owners.map((o) => o.id) } : {}),
      }),
      optimistic: () => setTasks(task.deliverableId, (ts) => ts.map((t) => (t.id === id ? apply(t) : t))),
      rollback: (before, optimistic) =>
        setTasks(task.deliverableId, (ts) =>
          ts.map((t) => {
            if (t.id !== id) return t;
            const next: Task = { ...t };
            if (changes.name !== undefined && t.name === optimistic.name) next.name = before.name;
            if (changes.status !== undefined && t.status === optimistic.status) next.status = before.status;
            if (changes.date !== undefined && t.date === optimistic.date) next.date = before.date;
            if (changes.ownerIds !== undefined && JSON.stringify(t.owners) === JSON.stringify(optimistic.owners)) next.owners = before.owners;
            return next;
          }),
        ),
      send: async (expected) => (await api.patchTask(id, { ...body, expected })).task,
      commit: (server) => setTasks(task.deliverableId, (ts) => ts.map((t) => (t.id === id ? server : t))),
    });
  }

  return { patchDeliverable, createDeliverable, patchGoal, createGoal, patchEpic, createEpic, createTask, patchTask };
}

export type Mutations = ReturnType<typeof createMutations>;
